"use client";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import React, { ReactNode, Children } from "react";
import { useCarousel } from "@/hooks/utility/useCarousel";
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { type EmblaCarouselType } from "embla-carousel";
import { cn } from "@/lib/utils";

interface CarouselWrapperProps {
  titleComponent?: ReactNode;
  titleWrapperClassName?: string;
  redirectUrl?: string;
  children: ReactNode;
  className?: string;
  itemClassName?: string;
  showNavigation?: boolean;
  containerClassName?: string;
  id?: string;
  showDots?: boolean;
  opts?: Parameters<typeof useEmblaCarousel>[1] | object;
  paddingTop?: boolean;
  paddingBottom?: boolean;
  removeContentMargins?: boolean;
  appContainer?: boolean;
  /** When false, carousel track spans full viewport width (title still uses appContainer). */
  contentAppContainer?: boolean;
  contentClassName?: string;
  setApi?: (api: EmblaCarouselType | undefined) => void;
}

export default function CarouselWrapper({
  titleComponent,
  titleWrapperClassName = "",
  children,
  className = "",
  itemClassName = "",
  showNavigation = true,
  containerClassName = "",
  id = "",
  showDots = false,
  opts = {},
  paddingTop = true,
  paddingBottom = true,
  appContainer = true,
  contentAppContainer,
  contentClassName = "",
  setApi,
}: CarouselWrapperProps) {
  const wrapContentInContainer = contentAppContainer ?? appContainer;
  const { carouselApi, setCarouselApi, currentIndex } = useCarousel();
  const itemsArray = Children.toArray(children);

  const handleSetApi = (api: EmblaCarouselType | undefined) => {
    setCarouselApi(api);
    if (setApi) {
      setApi(api);
    }
  };

  const getPaddingClasses = () => {
    const baseClasses = "w-full overflow-hidden bg-white";
    const mobilePadding =
      paddingTop && paddingBottom
        ? "py-8"
        : paddingTop
          ? "pt-8"
          : paddingBottom
            ? "pb-8"
            : "";
    const desktopPadding =
      paddingTop && paddingBottom
        ? "md:pt-10 md:pb-0"
        : paddingTop
          ? "md:pt-10"
          : paddingBottom
            ? "md:pb-0"
            : "";

    return `${baseClasses} ${mobilePadding} ${desktopPadding}`.trim();
  };

  return (
    <section id={id} className={cn(getPaddingClasses(), containerClassName)}>
      <Carousel
        opts={{
          align: "start",
          loop: false,
          skipSnaps: false,
          dragFree: true,

          ...opts,
        }}
        plugins={[WheelGesturesPlugin()]}
        setApi={handleSetApi}
        className={className}>
        <div className={cn(appContainer && "app-container")}>
          <div className="flex items-end justify-between">
            {titleComponent && (
              <div
                className={cn(
                  "flex items-center gap-2",
                  titleWrapperClassName
                )}>
                {titleComponent}
              </div>
            )}
            {showNavigation &&
              (carouselApi?.canScrollPrev() ||
                carouselApi?.canScrollNext()) && (
                <div className="hidden items-end gap-1 px-4 md:flex">
                  <CarouselPrevious className="hover:bg-gray-blue-100 relative top-0 left-0 h-8 w-8 translate-x-0 translate-y-0 cursor-pointer rounded-full border border-gray-200 bg-none md:h-10 md:w-10 lg:h-12 lg:w-12" />
                  <CarouselNext className="hover:bg-gray-blue-100 relative top-0 right-0 h-8 w-8 translate-x-0 translate-y-0 cursor-pointer rounded-full border border-gray-200 bg-none md:h-10 md:w-10 lg:h-12 lg:w-12" />
                </div>
              )}
          </div>
        </div>
        <CarouselContent
          className={cn(
            "flex pt-4 md:pt-10 md:pb-10",
            wrapContentInContainer && "app-container",
            contentClassName
          )}>
          {Array.isArray(children) ? (
            children.map((child, index) => (
              <CarouselItem
                key={index}
                className={`flex-shrink-0 pr-4 pl-0 ${itemClassName}`}>
                <div>{child}</div>
              </CarouselItem>
            ))
          ) : (
            <CarouselItem
              className={`flex-shrink-0 pr-4 pl-0 ${itemClassName}`}>
              <div>{children}</div>
            </CarouselItem>
          )}
        </CarouselContent>
      </Carousel>
      {showDots && itemsArray.length > 1 && (
        <div className="mt-4 flex justify-center sm:hidden">
          {itemsArray.map((_, idx) => (
            <button
              key={idx}
              className={`mx-1 h-2.5 w-2.5 rounded-full ${
                currentIndex === idx ? "bg-gray-600" : "border border-gray-600"
              }`}
              onClick={() => carouselApi?.scrollTo(idx)}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
